// src/pages/TermsOfSale.jsx
// Conditions générales de vente et d'abonnement ARTKIV.
// Style volontairement aligné sur src/pages/LegalNotice.jsx.
// ⚠️ À compléter avant publication : médiateur de la consommation et TVA intracommunautaire vérifiée.
import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-lg md:text-xl font-serif mb-3">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const BulletList = ({ children }) => (
  <ul className="mt-3 space-y-2 list-disc pl-5">{children}</ul>
);

export default function TermsOfSale() {
  return (
    <div className="bg-white text-black min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-sm leading-relaxed md:text-base md:leading-relaxed">
        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-serif mb-2">
            Conditions générales de vente et d’abonnement
          </h1>
          <div className="text-gray-500 text-xs md:text-sm">
            Dernière mise à jour : août 2026
          </div>
        </header>

        <div className="mb-10 border border-gray-200 bg-gray-50 px-4 py-4 text-gray-700">
          <p>
            Les présentes conditions régissent les abonnements et prestations ARTKIV.
            Elles complètent les conditions générales d’utilisation et la politique de
            confidentialité.
          </p>
        </div>

        <section className="space-y-8 text-gray-800">
          <Section title="1. Identité du prestataire">
            <p>
              Les services ARTKIV sont édités et commercialisés par <strong>QUANG&amp;CO</strong>,
              société à responsabilité limitée (SARL) au capital de 7 622,45 euros,
              immatriculée au Registre du commerce et des sociétés de Paris sous le
              numéro 435 386 107, dont le siège social est situé 231 rue Saint-Honoré,
              75001 Paris, France, ci-après « ARTKIV ».
            </p>
            <p>
              Représentant légal : Ngoc Nguyen Quang, gérant de QUANG&amp;CO.
            </p>
            <p>
              Contact :{" "}
              <a href="mailto:support@artkiv.com" className="underline">
                support@artkiv.com
              </a>
            </p>
          </Section>

          <Section title="2. Objet et champ d’application">
            <p>
              Les présentes conditions générales de vente et d’abonnement, ci-après les
              « CGV », définissent les conditions dans lesquelles ARTKIV fournit ses
              abonnements et prestations aux collectionneurs, Art Advisors, Family
              Offices, fondations, institutions et autres professionnels autorisés.
            </p>
            <p>Les services peuvent notamment comprendre :</p>
            <BulletList>
              <li>la gestion, la documentation et le suivi de collections d’œuvres d’art ;</li>
              <li>le stockage et le partage contrôlé de documents ;</li>
              <li>des outils de valorisation, de conservation et de reporting ;</li>
              <li>des fonctionnalités d’assistance par intelligence artificielle ;</li>
              <li>des prestations ponctuelles prévues dans un devis ou un bon de commande.</li>
            </BulletList>
            <p>
              Certaines dispositions sont réservées aux consommateurs et d’autres aux
              clients agissant pour les besoins de leur activité professionnelle.
            </p>
          </Section>

          <Section title="3. Documents contractuels et acceptation">
            <p>
              Avant toute commande, le Client peut consulter les présentes CGV, les
              caractéristiques essentielles du plan choisi, son prix, sa périodicité, sa
              durée et ses modalités de résiliation.
            </p>
            <p>
              La commande devient ferme lorsque le Client sélectionne une offre ou accepte
              un devis, vérifie le récapitulatif de commande, accepte les documents
              contractuels applicables et confirme la commande comportant, le cas échéant,
              une obligation de paiement.
            </p>
            <p>
              ARTKIV conserve la version acceptée, la date et la preuve de l’acceptation.
              Une confirmation est adressée au Client sur un support durable.
            </p>
            <p>
              Pour les Art Advisors, Family Offices, fondations et institutions, un accord
              de traitement des données, ou DPA, complète les présentes CGV.
            </p>
          </Section>

          <Section title="4. Plans et caractéristiques des services">
            <p>
              Les fonctionnalités, quotas, rôles, capacités de stockage, nombre
              d’utilisateurs, nombre de collections ou de clients gérés sont ceux affichés
              dans le récapitulatif de commande ou le devis au jour de la souscription.
            </p>
            <p>
              Les offres peuvent notamment être proposées sous les appellations Standard,
              Premium, Art Advisor, Art Advisor Premium ou Family Office. L’appellation
              commerciale d’un plan ne garantit aucune fonctionnalité qui ne serait pas
              expressément indiquée avant la commande.
            </p>
            <p>
              Les prototypes, démonstrations, feuilles de route et annonces de fonctions
              futures ne font pas partie du périmètre contractuel, sauf engagement exprès.
            </p>
          </Section>

          <Section title="5. Compte et sécurité">
            <p>
              Le Client fournit des informations exactes et maintient ses coordonnées à
              jour. Il protège ses identifiants et informe ARTKIV sans délai de tout accès
              non autorisé.
            </p>
            <p>
              Les accès sont nominatifs et ne doivent pas être partagés en dehors des
              fonctions de collaboration prévues. ARTKIV peut suspendre un accès compromis
              ou utilisé de manière manifestement abusive.
            </p>
          </Section>

          <Section title="6. Confidentialité des collections">
            <p>
              Les collections sont privées par défaut. ARTKIV n’accorde aucun accès à un
              Advisor, expert, Family Office, assureur, partenaire ou tiers sans une action
              ou une autorisation du Client, sauf obligation légale ou nécessité de sécurité.
            </p>
            <p>
              Le Client détermine les personnes autorisées, le périmètre partagé et le
              niveau de permission. Les environnements professionnels multi-clients font
              l’objet d’un cloisonnement logique entre dossiers.
            </p>
            <p>
              Les documents, valeurs, localisations, identités et informations patrimoniales
              non publiques sont confidentiels. Cette obligation se poursuit pendant cinq
              ans après la fin du contrat, sans préjudice de la protection plus longue des
              secrets d’affaires.
            </p>
          </Section>

          <Section title="7. Données et contenus du Client">
            <p>
              Le Client reste titulaire de ses données, photographies, documents et
              contenus. Il accorde à ARTKIV les seuls droits techniques nécessaires à
              l’hébergement, la sauvegarde, l’affichage, l’analyse demandée et la fourniture
              du service pendant la durée du contrat.
            </p>
            <p>
              ARTKIV ne vend pas les données de collection. Les contenus privés ne sont pas
              utilisés pour entraîner un modèle d’intelligence artificielle généraliste
              sans dispositif distinct et consentement approprié lorsqu’il est requis.
            </p>
            <p>
              Le Client garantit disposer des droits et autorisations nécessaires sur les
              contenus déposés. Pour en savoir plus, consultez notre{" "}
              <Link to="/privacy-policy" className="underline">
                politique de confidentialité
              </Link>.
            </p>
          </Section>

          <Section title="8. Intelligence artificielle et limites des résultats">
            <p>
              Les classifications, détections, rapprochements, valorisations indicatives,
              alertes, résumés, extractions OCR et recommandations automatisées sont des
              outils d’assistance. Leurs résultats peuvent être incomplets, incertains ou
              erronés et dépendent de la qualité des données fournies.
            </p>
            <p>Sauf mention expresse dans un rapport signé par un professionnel habilité, ARTKIV ne fournit pas :</p>
            <BulletList>
              <li>une expertise ou une authentification juridiquement opposable ;</li>
              <li>une garantie d’authenticité, d’attribution, de provenance, d’état ou de valeur ;</li>
              <li>un conseil juridique, fiscal, successoral, assurantiel ou financier personnalisé ;</li>
              <li>une promesse de prix de vente, de liquidité ou de performance.</li>
            </BulletList>
            <p>
              Toute décision sensible doit être confirmée par le professionnel compétent.
              Les rapports indiquent lorsqu’ils ont été produits ou structurés avec
              l’assistance d’une intelligence artificielle.
            </p>
          </Section>

          <Section title="9. Utilisation de l’IA par un professionnel">
            <p>
              Lorsqu’un Art Advisor, Family Office, une fondation ou une institution utilise
              ARTKIV pour le compte d’un tiers, il confirme être autorisé à déposer et
              traiter les données concernées et avoir informé son client de l’utilisation
              éventuelle d’outils d’intelligence artificielle.
            </p>
            <p>
              Les traitements particulièrement sensibles, notamment le matching
              patrimonial ou psychologique entre des héritiers et des œuvres, ne doivent
              pas être activés sans les autorisations spécifiques requises. Ils ne
              constituent jamais une décision de répartition ou un conseil successoral.
            </p>
          </Section>

          <Section title="10. Experts, partenaires et mises en relation">
            <p>
              ARTKIV peut faciliter une mise en relation avec un expert, restaurateur,
              transporteur, assureur, Advisor ou autre partenaire indépendant. Sauf
              indication contraire, le contrat de mission est conclu directement entre le
              Client et le partenaire, qui demeure responsable de son intervention, de ses
              honoraires et de ses assurances.
            </p>
            <p>
              ARTKIV ne perçoit aucune commission sur les missions réalisées par les Art
              Advisors dans le cadre de leurs propres relations clients. Toute rémunération
              liée à une mise en relation ou à un service partenaire est annoncée avant la
              commande concernée.
            </p>
          </Section>

          <Section title="11. Prix, taxes et facturation">
            <p>
              Les prix applicables sont ceux affichés en euros lors de la commande ou
              figurant sur le devis. Les prix destinés aux consommateurs sont présentés
              toutes taxes comprises. Les prix professionnels peuvent être indiqués hors
              taxes, la TVA applicable étant ajoutée.
            </p>
            <p>
              La facturation est effectuée par QUANG&amp;CO, notamment au moyen de Stripe
              Billing et/ou Pennylane. Les prestations de tiers, imports sur mesure ou
              dépassements ne sont facturés que s’ils ont été annoncés ou acceptés.
            </p>
          </Section>

          <Section title="12. Paiement et retard">
            <p>
              Le Client autorise le paiement récurrent aux échéances annoncées. En cas
              d’échec, ARTKIV peut renouveler la tentative et inviter le Client à mettre à
              jour son moyen de paiement. À défaut de régularisation, l’accès peut être
              limité ou suspendu sans suppression immédiate des données.
            </p>
            <p>
              Pour les Clients professionnels, tout retard entraîne les pénalités indiquées
              sur la facture et l’indemnité forfaitaire légale de 40 euros pour frais de
              recouvrement, sans préjudice d’une indemnisation complémentaire justifiée.
            </p>
          </Section>

          <Section title="13. Essai gratuit de 14 jours">
            <p>
              Lors de son premier abonnement ARTKIV, le Client bénéficie d’une période
              d’essai gratuite de quatorze jours calendaires, sauf conditions plus
              favorables affichées lors de la souscription.
            </p>
            <p>
              Avant la confirmation, ARTKIV indique le plan concerné, le prix qui sera
              prélevé à l’issue de l’essai, la périodicité, la date du premier paiement et
              les modalités d’annulation.
            </p>
            <p>
              Le Client peut annuler pendant l’essai depuis son compte ou en écrivant à{" "}
              <a href="mailto:support@artkiv.com" className="underline">
                support@artkiv.com
              </a>. Une annulation effectuée avant son expiration empêche tout premier
              prélèvement. À défaut, l’abonnement devient payant au terme de l’essai.
            </p>
            <p>
              L’essai commercial ne se confond pas avec le droit légal de rétractation.
              ARTKIV ne propose pas de garantie commerciale « 30 jours satisfait ou
              remboursé ».
            </p>
          </Section>

          <Section title="14. Durée, renouvellement et changement de plan">
            <p>
              Le contrat est conclu pour la période indiquée lors de la commande. Sauf
              formule à durée ferme non renouvelable, il se renouvelle pour des périodes de
              même durée jusqu’à résiliation.
            </p>
            <p>
              Une montée en gamme peut prendre effet immédiatement selon les conditions
              indiquées avant confirmation. Une baisse de plan prend effet à l’échéance
              suivante. ARTKIV informe le Client des données ou fonctions susceptibles de
              ne plus être accessibles et lui permet de les exporter.
            </p>
          </Section>

          <Section title="15. Résiliation">
            <p>
              Le Client peut résilier son abonnement depuis son compte au moyen de la
              fonctionnalité de résiliation en ligne ou en écrivant à{" "}
              <a href="mailto:support@artkiv.com" className="underline">
                support@artkiv.com
              </a>.
            </p>
            <p>
              La résiliation empêche le renouvellement et prend effet à la fin de la
              période déjà payée, sauf droit légal ou condition plus favorable. La
              résiliation de l’abonnement et la suppression du compte sont deux opérations
              distinctes.
            </p>
          </Section>

          <Section title="16. Droit de rétractation des consommateurs">
            <p>
              Le Client consommateur dispose d’un délai de quatorze jours à compter de la
              conclusion du contrat à distance pour exercer son droit de rétractation, sans
              avoir à motiver sa décision.
            </p>
            <p>
              Il peut utiliser le formulaire figurant à la fin des présentes CGV ou
              adresser une déclaration dénuée d’ambiguïté à QUANG&amp;CO, 231 rue
              Saint-Honoré, 75001 Paris, France, ou à{" "}
              <a href="mailto:support@artkiv.com" className="underline">
                support@artkiv.com
              </a>, avec l’objet « Rétractation ARTKIV ».
            </p>
            <p>
              Si le Client demande expressément l’exécution du service avant la fin du
              délai et se rétracte ensuite, il peut être tenu de payer la part du service
              effectivement fournie dans les conditions prévues par la loi.
            </p>
            <p>
              Les remboursements dus sont effectués dans les quatorze jours suivant la
              notification, par le même moyen de paiement sauf accord contraire.
            </p>
          </Section>

          <Section title="17. Suspension et résiliation par ARTKIV">
            <p>
              ARTKIV peut suspendre tout ou partie du service en cas de risque de sécurité,
              utilisation illicite, violation grave de la confidentialité, atteinte aux
              droits de tiers, contournement des quotas ou impayé persistant.
            </p>
            <p>
              Sauf urgence, le Client est informé du motif et dispose d’un délai raisonnable
              pour remédier au manquement. Un manquement grave non réparé après mise en
              demeure peut entraîner la résiliation.
            </p>
          </Section>

          <Section title="18. Disponibilité, maintenance et assistance">
            <p>
              ARTKIV met en œuvre des moyens raisonnables pour assurer la disponibilité, la
              sécurité et la continuité du service. Des interruptions peuvent être
              nécessaires pour maintenance, mise à jour, sécurité, force majeure ou
              défaillance d’un fournisseur essentiel.
            </p>
            <p>
              Sauf engagement écrit dans un contrat professionnel, aucun taux de
              disponibilité, délai de réponse 24/7 ou délai de résolution garanti n’est
              promis.
            </p>
          </Section>

          <Section title="19. Export et fin de contrat">
            <p>
              À la fin du contrat, ARTKIV maintient pendant trente jours calendaires un
              accès limité à l’export ou fournit un moyen raisonnable de récupération des
              données. Pendant cette période, le compte est placé en lecture seule : aucune
              donnée ne peut être ajoutée ou modifiée et aucune nouvelle analyse ne peut
              être lancée.
            </p>
            <p>
              ARTKIV informe le Client de la date limite d’export et de la suppression
              programmée. À l’issue du délai, les données peuvent être supprimées ou
              anonymisées selon la politique de confidentialité et les obligations légales.
            </p>
          </Section>

          <Section title="20. Garanties légales des services numériques">
            <p>
              Le Client consommateur bénéficie des garanties légales impératives
              applicables aux contenus et services numériques, notamment en cas de défaut
              de conformité. ARTKIV fournit les mises à jour nécessaires au maintien de la
              conformité pendant la période légalement requise.
            </p>
            <p>
              Ces garanties s’appliquent indépendamment de toute garantie commerciale.
            </p>
          </Section>

          <Section title="21. Responsabilité">
            <p>
              ARTKIV répond des dommages directs qui lui sont imputables. ARTKIV ne répond
              pas des décisions prises sur la seule base d’une estimation automatisée,
              d’une alerte ou d’un contenu indicatif, ni des actes d’un partenaire
              indépendant avec lequel le Client contracte directement.
            </p>
            <p>
              Pour les Clients professionnels seulement et sous réserve des domaines où
              une limitation est interdite, la responsabilité totale d’ARTKIV au titre
              d’une période de douze mois est limitée au montant hors taxes effectivement
              payé à ARTKIV pendant cette période.
            </p>
            <p>
              Aucune limitation ne prive un Client consommateur de ses droits impératifs.
            </p>
          </Section>

          <Section title="22. Force majeure">
            <p>
              Aucune partie n’est responsable d’un manquement causé par un événement
              répondant aux critères légaux de la force majeure. Si l’empêchement se
              prolonge plus de soixante jours, chaque partie peut mettre fin aux prestations
              affectées, sans pénalité, sous réserve des remboursements légalement dus.
            </p>
          </Section>

          <Section title="23. Modification des CGV et des prix">
            <p>
              ARTKIV peut modifier les CGV pour tenir compte d’une évolution légale,
              réglementaire, technique ou fonctionnelle. La version applicable à une
              commande demeure accessible sur un support durable.
            </p>
            <p>
              Toute hausse de prix ou modification substantielle défavorable d’un
              abonnement en cours est notifiée avec un préavis de trente jours, sauf délai
              différent imposé par la loi. Le Client peut résilier avant son entrée en
              vigueur.
            </p>
          </Section>

          <Section title="24. Réclamations et médiation">
            <p>
              Toute réclamation doit d’abord être adressée à{" "}
              <a href="mailto:support@artkiv.com" className="underline">
                support@artkiv.com
              </a>{" "}
              ou par courrier à QUANG&amp;CO, 231 rue Saint-Honoré, 75001 Paris, France.
            </p>
            <p>
              Si la réponse ne satisfait pas le Client consommateur, celui-ci peut saisir
              gratuitement le médiateur de la consommation dont relève ARTKIV.
            </p>
            {/* TODO JURIDIQUE : ajouter ici le nom, l'adresse et le site du médiateur
                uniquement après adhésion effective de QUANG&CO. */}
          </Section>

          <Section title="25. Droit applicable et juridiction">
            <p>Les présentes CGV sont régies par le droit français.</p>
            <p>
              Pour les consommateurs, les juridictions compétentes sont déterminées par
              les règles impératives applicables. Pour les Clients professionnels, après
              tentative de résolution amiable, les tribunaux compétents de Paris sont seuls
              compétents, sous réserve des règles impératives.
            </p>
          </Section>

          <Section title="26. Langue faisant foi">
            <p>
              La version française des présentes CGV constitue la version juridiquement
              applicable. Toute traduction est fournie pour faciliter la compréhension. En
              cas de contradiction, la version française prévaut, sous réserve des règles
              impératives applicables.
            </p>
          </Section>

          <Section title="Formulaire type de rétractation">
            <p>
              À compléter et renvoyer uniquement si vous souhaitez vous rétracter du
              contrat.
            </p>
            <div className="border border-gray-200 bg-gray-50 px-4 py-4 space-y-2">
              <p>
                <strong>À l’attention de :</strong> QUANG&amp;CO — ARTKIV, 231 rue
                Saint-Honoré, 75001 Paris, France — support@artkiv.com
              </p>
              <p>
                Je vous notifie par la présente ma rétractation du contrat portant sur la
                prestation ou le service numérique ci-dessous.
              </p>
              <p>Plan ou prestation :</p>
              <p>Commandé le :</p>
              <p>Nom du Client :</p>
              <p>Adresse du Client :</p>
              <p>Adresse e-mail du compte :</p>
              <p>Date :</p>
              <p>Signature, uniquement en cas d’envoi papier :</p>
            </div>
          </Section>
        </section>

        <footer className="mt-12 pt-6 border-t border-gray-200 text-xs md:text-sm text-gray-500">
          <p>
            Voir également nos{" "}
            <Link to="/legal-notice" className="underline">
              mentions légales
            </Link>{" "}
            et notre{" "}
            <Link to="/privacy-policy" className="underline">
              politique de confidentialité
            </Link>.
          </p>
        </footer>
      </div>
    </div>
  );
}
